/**
 * Custom component for Romanza Wines by the Glass section.
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.6.3
 * @copyright  Copyright (C) 2022-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.v
 *    2023-04-17 (1.1.0) (jhoward)
 *      - Menu definitions now include: ItemGroupPricingTypes interface, ItemGroupShowBIN interface, PreviousGroupAndSubgroupTitles interface and VisualMenuSectionComponent interface.
 *
 *    2023-04-24 (1.2.0) (jhoward)
 *      - Menu definitions now include: MenuTag interface and UpdateMenuResponse interface.
 *      - Add items_with_tags property to Menu interface.
 *
 *    2023-06-15 (1.3.0) (jhoward)
 *      - Add ms_show_pricing_header property to MenuSection interface.
 *
 *    2023-09-08 (1.3.1) (jhoward)
 *      - Add ms_show_bin property to MenuSection interface.
 *
 *    2023-10-05 (1.3.2) (jhoward)
 *      - Add mi_multi_line_price property to MenuItem interface.
 *
 *    2023-12-08 (1.3.3) (zsahs)
 *      - Add lastMACUpdate to VisualMenuState interface.
 *
 *    2024-06-10 (1.3.4) (jhoward)
 *      - Rename MenuTag interface to MenuTagModel.
 *
 *    2024-07-09 (1.3.5) (jhoward)
 *      - Refactor SectionOffsetStructure from <visual-menu> to menu defs.ts.
 *      - Add menu_items property to MenuSection interface in menu defs.ts.
 *
 *    2024-07-18 (1.4.0) (jhoward)
 *      - Refactor VisualMenuComponent from <visual-menu> to menu defs.ts.
 *      - Refactor TextualMenuComponent from <textual-menu> to menu defs.ts.
 *      - Refactor VisualMenuDefaultItemComponent from <default-item> to menu defs.ts.
 *      - Refactor VisualMenuPageComponent from <menu-page> to menu defs.ts.
 *      - Refactor VisualMenuItemGroupComponent from <menu-item-group> to menu defs.ts.
 *      - Refactor VisualMenuSectionComponent from <default-section> to menu defs.ts.
 *      - Refactor SectionStructure from <default-section> to menu defs.ts.
 *
 *    2024-08-19 (1.5.0) (jhoward)
 *      - Add TextualMenuState interface to menu defs.ts.
 *      - Add TextualMenuDefaultItemComponent interface to menu defs.ts.
 *      - Modify VisualMenuSectionComponent interface in menu defs.ts.
 *      - Add TextualMenuSectionComponent interface to menu defs.ts.
 *      - Add MenuSectionListItemComponent interface to menu defs.ts.
 *
 *    2024-08-26 (1.5.1) (jhoward)
 *      - Add selectedMenuItemIds to VisualMenuState interface in menu defs.ts.
 *      - Add selectedMenuItemIds to TextualMenuState interface in menu defs.ts.
 *      - Add isFavoriteHintShown to TextualMenuState interface in menu defs.ts.
 *      - Remove toggleMenuItemSelected from TextualMenuDefaultItemComponent interface in menu defs.ts.
 *      - Remove selectedMenuItemIds from TextualMenuDefaultItemComponent interface in menu defs.ts.
 *      - Remove toggleMenuItemSelected from TextualMenuSectionComponent interface in menu defs.ts.
 *      - Remove selectedMenuItemIds from TextualMenuSectionComponent interface in menu defs.ts.
 *
 *    2024-10-24 (1.5.2) (jhoward)
 *      - Add menu_textual_only to Menu interface in menu defs.ts.
 *
 *    2025-01-24 (1.5.3) (jhoward)
 *      - Add the deviceMacAddress, deviceMacAddressMessage, networkErrorMessage, and isAdminView properties to the VisualMenuState interface in menu defs.ts.
 *      - Add the deviceMacAddress, deviceMacAddressMessage, networkErrorMessage, and isAdminView properties to the TextualMenuState interface in menu defs.ts.
 *
 *    2025-02-12 (1.5.4) (jhoward)
 *      - Remove menu_show_date and menu_hide_date from the Menu interface in menu defs.ts.
 *      - Remove the `schedules` property from the Menu, MenuSection, and MenuItem interfaces in menu defs.ts.
 *      - Add the `textual_schedules` and `visual_schedules` property to the Menu, MenuSection, and MenuItem interfaces in menu defs.ts.
 *      - Add the MenuTagLegendComponent interface in menu defs.ts.
 *      - Modify the MenuScheduleNormalized interface to extend the MenuSchedule interface with schedule_start_timestamp and schedule_end_timestamp rather than overriding existing properties in menu defs.ts.
 *
 *    2025-04-08 (1.6.0) (jhoward)
 *      - Remove the "items_with_tags" property from the "Menu" interface in menu defs.ts.
 *      - Add the "schedules" property to the "Menu" interface in menu defs.ts.
 *      - Add the "menu_id" property to the "MenuSection" interface in menu defs.ts.
 *      - Add the "schedules" property to the "MenuSection" interface in menu defs.ts.
 *      - Remove the "menu_items" property from the "MenuSection" interface in menu defs.ts.
 *      - Add the "section_tags" property to the "MenuSection" interface in menu defs.ts.
 *      - Add the "price_label" property to the "MenuItem" interface in menu defs.ts.
 *      - Add the "schedules" property to the "MenuItem" interface in menu defs.ts.
 *      - Add the "MenuItemWithNormalizedSchedules" interface to denote post-processed menu items in menu defs.ts.
 *      - Add the "reset" method to the "MenuSwitcherComponent" interface in menu defs.ts.
 *      - Add the "displayType" property to the "VisualMenuState" interface in menu defs.ts.
 *      - Add the "displayType" property to the "TextualMenuState" interface in menu defs.ts.
 *      - Remove the "initialPage" property from the "VisualMenuComponent" interface in menu defs.ts.
 *      - Remove the "mobileLayoutThreshold" property from the "VisualMenuComponent" interface in menu defs.ts.
 *      - Combine the "VisualMenuDefaultItemComponent" and "TextualMenuDefaultItemComponent" interfaces into the "MenuDefaultItemComponent" interface in menu defs.ts.
 *      - Modify the "page" property on the "VisualMenuPageComponent" component to be an array of "MenuSection" objects in menu defs.ts.
 *      - Modify the "VisualMenuSectionComponent", "TextualMenuSectionComponent", and "MenuTagLegendComponent" interfaces to set the "items" property to an array of MenuItemWithNormalizedSchedules objects in menu defs.ts.
 *      - Remove the "SectionStructure" interface in menu defs.ts.
 *      - Add the "SearchMenuComponent" interface in menu defs.ts.
 *      - Add the "SearchMenuEmitViewMenuItemPayload" interface in menu defs.ts.
 *      - Refactor the "ALLOWED_ITEM_COMPONENTS" type to menu defs.ts.
 *      - Refactor the "WINE_LIST_ITEM_COMPONENTS" type to menu defs.ts.
 *      - Add the "MENU_DISPLAY_TYPE" type to menu defs.ts.
 *      - Add the "MENU_ITEM_TAG_LOCATIONS" type to menu defs.ts.
 *      - Add the "MENU_ITEM_LAYOUT" type to menu defs.ts.
 *      - Add the "MENU_ITEM_GROUP_LAYOUT" type to menu defs.ts.
 *      - Add the "SECTION_CONTENT_AREA_TOP_LAYOUT" type to menu defs.ts.
 *      - Add the "SECTION_CONTENT_AREA_BOTTOM_LAYOUT" type to menu defs.ts.
 *
 *    2025-05-05 (1.6.1) (jhoward)
 *      - Add "easter-food-item" to ALLOWED_ITEM_COMPONENTS in defs.ts.
 *      - Add "mothers-day-food-item" to ALLOWED_ITEM_COMPONENTS in defs.ts.
 *      - Add MENU_SYSTEM_VERSION constant in defs.ts.
 *      - Add the optionAllowListHiddenMenuIds property to the VisualMenuState interface in defs.ts.
 *      - Add the optionAllowListHiddenMenuIds property to the TextualMenuState interface in defs.ts.
 *
 *    2025-05-05 (1.6.2) (jhoward)
 *      - Add the ms_disable_pricing_types property to the MenuSection interface in defs.ts.
 *      - Add the menu_property property to the Menu interface in defs.ts.
 *
 * 2026-02-17 (1.6.3) (jhoward)
 *   - Add 'chi-chinese-new-year-item' to App\Library\Js\Components\Menu::ALLOWED_ITEM_COMPONENTS array, enabling new menu item type.
 */

import {Page} from "../defs";
import {ComponentPublicInstance} from "vue";

export const MENU_SYSTEM_VERSION = '1.0.0';

export interface Menu {
  base_url: string;
  flipbook_pages: [];
  full_title: string;
  menu_content_area_bottom: string;
  menu_content_area_top: string;
  menu_custom_flipbook_component: string;
  menu_google_drive_id: string;
  menu_id: number;
  menu_pdf_filename: string;
  menu_pdf_page_count: number;
  menu_prioritize_time_of_day_end: number;
  menu_prioritize_time_of_day_hours: string;
  menu_prioritize_time_of_day_start: number
  menu_show_on_outlet_page: number;
  menu_status: string;
  menu_type: MenuType;
  menu_version: string;
  menu_icon: string;
  mt_id: number;
  page: Page
  page_id: number;
  sections: MenuSection[];
  schedules?: MenuSchedule[];//for admin requests only
  textual_schedules?: MenuSchedule[];
  visual_schedules?: MenuSchedule[];
  normalizedSchedules?: MenuScheduleNormalized[];
  menu_textual_only: number;
  menu_property: 'wv' | 'reno';
}

export interface MenuType {
  mt_id: number;
  mt_order: number;
  mt_title: string;
}

export interface MenuSection {
  children: MenuSection[];
  grouped_items: GroupedMenuItems;
  ms_content_area_bottom: string;
  ms_content_area_top: string;
  ms_custom_flipbook_component: string;
  ms_group_by_price: 0 | 1;
  ms_show_pricing_header: 0 | 1;
  ms_disable_pricing_types: 0 | 1;
  ms_show_bin: 0 | 1;
  ms_highlight: 0 | 1;
  ms_id: number;
  menu_id: number;
  ms_menu_version_override: null
  ms_nav_title: string;
  ms_parent_id: number;
  ms_show_in_nav: 0 | 1;
  ms_subtitle: string;
  ms_title: string;
  status: string;
  schedules?: MenuSchedule[];//for admin requests only
  textual_schedules?: MenuSchedule[];
  visual_schedules?: MenuSchedule[];
  normalizedSchedules?: MenuScheduleNormalized[];
  // menu_items?: MenuItem[];
  section_tags: MenuTagModel[];
}

export interface GroupedMenuItems {
  [key: string]: {
    [key: string]: MenuItemWithNormalizedSchedules[]
  }
}

export interface ItemGroupPricingTypes {
  [key: string]: {
    [key: string]: string[];
  }
}

export interface ItemGroupShowBIN {
  [key: string]: {
    [ky: string]: boolean;
  }
}

export interface MenuItem {
  mi_custom_flipbook_component: string;
  mi_description: string;
  mi_highlight: number;
  mi_id: number;
  mi_item_group_name: string;
  mi_item_subgroup_name: string;
  mi_market_price: number;
  mi_multi_line_price: number;
  mi_order: number;
  mi_price: string;
  mi_subtitle: string;
  mi_title: string;
  ms_id: number;
  price_display: string;
  price_label: string;
  status: string;
  tags: MenuTagModel[];
  schedules?: MenuSchedule[];//for admin requests only
  textual_schedules?: MenuSchedule[];
  visual_schedules?: MenuSchedule[];
}

export interface MenuItemWithNormalizedSchedules extends MenuItem {
  normalizedSchedules: MenuScheduleNormalized[];
}

export interface MenuTagModel {
  mt_id: number;
  mt_title: string;
  mt_icon_class: string;
  deleted_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  html?: string;
}

export interface PreviousGroupAndSubgroupTitles {
  lastGroupTitle: string;
  lastSubgroupTitle: string;
}

export interface MenuSwitcherComponent extends ComponentPublicInstance {
  isOpen: boolean;
  switcherHeight: number;
  minHeight: number;
  maxHeight: number;
  reset: () => void;
}

export interface UpdateMenuResponse {
  STATUS: string;
  ERRORS: string[];
  MSS: string;
  DATA: {
    CONTENT_HASH?: string;
    RELATED_MENUS?: Menu[];
    DEVICE_ASSET_CODE?: string;
    MENU?: Menu;
    LOAD_TIME?: string;
  };
  SETTINGS?: {
    SEARCH?: {
      INDEX?: string;
      KEY?: string;
      HOST?: string;
    };
    CONTENT_CHECK?: {
      TIMEOUT?: number;
      DURATION?: number;
    };
    USER_IS_ADMIN?: boolean;
  };
}

export interface MenuSchedule {
    schedule_id: number;
    schedule_title: string;
    schedule_model_type: string;
    schedule_model_id: number;
    schedule_days_of_week: number[];
    schedule_day_start_time: number | null;
    schedule_day_end_time: number | null;
    schedule_start_datetime: string | null;
    schedule_end_datetime: string | null;
    schedule_is_textual: boolean;
    schedule_is_visual: boolean;
    deleted_at: string | null;
    updated_at: string;
    created_at: string;
}

export interface MenuScheduleNormalized extends MenuSchedule {
    schedule_start_timestamp: number;
    schedule_end_timestamp: number;
}

export interface VisualMenuState {
  currentDate: Date;
  currentDateTimeUtc: number;
  currentDayOfWeek: number;
  currentTimeOfDay: number;
  dateTimer: number;
  dateTimeout: number;
  isOptionsOpen: boolean
  optionMenuTimer: number;
  optionMenuTimeout: number;
  optionAllowListHiddenMenuIds: number[];
  lastContentUpdate: Date | null;
  lastMACUpdate: Date | null;
  lastContentUpdateDateTime: string;
  lastContentHash: string;
  isReportIssueDialogOpen: boolean;
  selectedMenuItemIds: number[];
  deviceMacAddress: string;
  deviceMacAddressMessage: string;
  networkErrorMessage: string;
  isAdminView: boolean;
  displayType: 'visual';
}

export interface TextualMenuState {
  currentDate: Date;
  currentDateTimeUtc: number;
  currentDayOfWeek: number;
  currentTimeOfDay: number;
  dateTimer: number;
  dateTimeout: number;
  selectedMenuItemIds: number[];
  isFavoriteHintShown: boolean;
  deviceMacAddress: string;
  deviceMacAddressMessage: string;
  networkErrorMessage: string;
  isAdminView: boolean;
  displayType: 'textual';
  optionAllowListHiddenMenuIds: number[];
}

export interface SectionOffsetStructure {
  el: HTMLElement | null;
  id: number;
  offsetTop: number;
  hash: string
}

export interface VisualMenuComponent extends ComponentPublicInstance {
  switchToRelatedMenu: (relatedMenuId: number) => void;
  menu: Menu | null;
  menuId: number;
  // initialPage: number;
  // type: string;
  sectionOffsets: SectionOffsetStructure[];
  scrollToSection: (sectionOffset: SectionOffsetStructure, isSmooth?: boolean) => void;
  isAdminView: boolean;
  getMenuData: (menuId: number, contentHash: string, menuType: string, isDev: boolean, checkAssetVersions: boolean) => Promise<UpdateMenuResponse | null>;
  isModelScheduled: (normalizedSchedules: MenuScheduleNormalized[]) => boolean;
  apiHostname: string;
}

export interface TextualMenuComponent extends ComponentPublicInstance {
  switchToRelatedMenu: (relatedMenuId: number) => void;
  menu: Menu | null;
  menuId: number;
  type: string;
  menuItems: { [key: number]: MenuItem };
  sectionOffsets: SectionOffsetStructure[];
  scrollToSection: (sectionOffset: SectionOffsetStructure, isSmooth?: boolean) => void;
  isAdminView: boolean;
  getMenuData: (menuId: number, contentHash: string, menuType: string, isDev: boolean, checkAssetVersions: boolean) => Promise<UpdateMenuResponse | null>;
  isModelScheduled: (normalizedSchedules: MenuScheduleNormalized[]) => boolean;
  apiHostname: string;
}

export interface MenuDefaultItemComponent extends ComponentPublicInstance {
  item: MenuItemWithNormalizedSchedules;
}

export interface VisualMenuPageComponent extends ComponentPublicInstance {
  page: MenuSection[];
  pageIndex: number;
  isLastPage: boolean;
  isHardPage: boolean;
}

export interface VisualMenuItemGroupComponent {
  section: MenuSection;
  menuVersion: string;
  getPreviousGroupAndSubgroupTitles: (groupTitle: string, subgroupTitle: string) => PreviousGroupAndSubgroupTitles | null;
  itemGroupPricingTypes: ItemGroupPricingTypes;
  normalizedSchedules: MenuScheduleNormalized[];
}

// export interface SectionItemSubgroupComponent extends ComponentPublicInstance {
//   items: MenuItem[];
//   groupTitle: string;
//   subgroupTitle: string;
//   menuItemComponent: string;
//   section: MenuSection;
//   layout: MENU_ITEM_GROUP_LAYOUT;
// }

export interface VisualMenuSectionComponent extends ComponentPublicInstance {
  section: MenuSection;
  items: MenuItemWithNormalizedSchedules[];
  menu: Menu;
  defaultMenuItemComponentOverride: string;
}

export interface TextualMenuSectionComponent extends ComponentPublicInstance {
  section: MenuSection;
  items: MenuItemWithNormalizedSchedules[];
  menu: Menu;
  itemGroupPricingTypes: ItemGroupPricingTypes;
}

export interface MenuSectionListItemComponent extends ComponentPublicInstance {
  section: MenuSection;
}

export interface MenuTagLegendComponent extends ComponentPublicInstance {
  items: MenuItemWithNormalizedSchedules[];
}

export interface MenuSwitcherComponent extends ComponentPublicInstance {
  menus: Menu[];
  isModelScheduled: (normalizedSchedules: MenuScheduleNormalized[]) => boolean;
}

export interface MenuOptionsComponent extends ComponentPublicInstance {
  menus: Menu[];
  isModelScheduled: (normalizedSchedules: MenuScheduleNormalized[]) => boolean;
}

export interface SearchMenuComponent extends ComponentPublicInstance {
  reset: (force?: boolean) => void;
  isModelScheduled: (normalizedSchedules: MenuScheduleNormalized[]) => boolean;
}

export interface SearchMenuEmitViewMenuItemPayload {
  menu_id: number;
  mi_id: number;
}

export const ALLOWED_ITEM_COMPONENTS: string[] = [
  'back-bar-default-item',
  'bimini-food-item',
  'bimini-steak-item',
  'bimini-cocktail-item',
  'chi-default-item',
  'chi-chinese-new-year-item',
  'chi-wine-list-item',
  'menu-item-text-only',
  'menu-item-highlighted-box',
  'high-limit-food-item',
  'oceano-default-item',
  'pancho-willies-food-item',
  'pancho-willies-drink-item',
  'pancho-willies-cervezas-item',
  'wv-steak-house-food-item',
  'wv-steak-house-steak-item',
  'wv-steak-house-cocktail-item',
  'wv-steak-house-signature-drink-item',
  'wv-steak-house-wine-list-item',
  'thanksgiving-food-item',
  'new-years-food-item',
  'christmas-food-item',
  'christmas-drink-item',
  'valentines-day-food-item',
  'default-item',
  'wine-list-item',
  'romanza-default-item',
  'easter-food-item',
  'mothers-day-food-item',
  'mothers-day-cocktail-item'
];

export const WINE_LIST_ITEM_COMPONENTS = [
  'wine-list-item',
  'chi-wine-list-item',
  'wv-steak-house-wine-list-item',
];

export type MENU_DISPLAY_TYPE = 'visual' | 'textual';
export type MENU_ITEM_TAG_LOCATIONS = '' | 'after-title' | 'before-title';
export type MENU_ITEM_LAYOUT = '' | 'price-inline' | 'price-below';
export type MENU_ITEM_GROUP_LAYOUT = '' | 'title-above';
export type SECTION_CONTENT_AREA_TOP_LAYOUT = '' | 'above-title' | 'none';
export type SECTION_CONTENT_AREA_BOTTOM_LAYOUT = '' | 'none';